import type { FC } from 'react';
import BrikAgente from '../BrikAgente';
import { useCelular } from '../../hooks/useCelular';

/**
 * As cenas do guia de boas-vindas (desenho fechado em 28/10/2026, seção 5 de
 * docs/propostas/entrada/boas-vindas.html). O Brik é o `BrikAgente` de sempre;
 * em volta dele, um desenho por passo, nas cores do próprio Brik:
 *
 * 1 · Oi — o Brik feliz pulando de leve; um cartão de conversa, um pedaço de
 *     funil e um "feito" flutuam, cada um no seu ritmo.
 * 2 · Canal — as mensagens pipocam na tela do celular, a linha tracejada corre
 *     do celular até o Brik com um ponto viajando nela; o Brik olha em volta.
 * 3 · No ar — o Brik comemorando e os tijolinhos de confete caindo.
 *
 * As cenas ficam VIVAS enquanto o passo está aberto (decisão dele em 28/10) e
 * param com "reduzir movimento" (o CSS cuida).
 */

/* Os tijolinhos do passo 3: posição no palco de 672×160, tamanho e cor (as do Brik e o ciano da marca). */
const CONFETE: { x: number; y: number; w: number; h: number; cor: string }[] = [
    { x: 236, y: 26, w: 12, h: 7, cor: '#5b9bff' },
    { x: 272, y: 12, w: 10, h: 6, cor: '#00f0ff' },
    { x: 330, y: 7, w: 9, h: 5.5, cor: '#031f4c' },
    { x: 392, y: 10, w: 12, h: 7, cor: '#00f0ff' },
    { x: 430, y: 30, w: 9, h: 5.5, cor: '#5b9bff' },
    { x: 300, y: 36, w: 8, h: 5, cor: '#0968fe' },
];

const CenaBoasVindas: FC<{ passo: 1 | 2 | 3 }> = ({ passo }) => {
    const celular = useCelular();
    const alturaBrik = passo === 2 ? (celular ? 62 : 92) : (celular ? 72 : 104);

    return (
        <div className={`bv-palco is-passo-${passo}`} aria-hidden="true">
            {passo === 1 && (
                <svg className="bv-cena" viewBox="0 0 672 160">
                    {/* o cartão de conversa, o pedaço de funil e o "feito": grupo de posição por
                        fora, grupo animado por dentro (a animação CSS substitui o transform do SVG) */}
                    <g transform="translate(140 48)">
                        <g className="bv-flo">
                            <rect width="100" height="50" rx="9" fill="#ffffff" stroke="#e5e5e1" strokeWidth="1.5" />
                            <rect x="12" y="14" width="50" height="7" rx="3.5" fill="#dfecff" />
                            <rect x="12" y="29" width="72" height="7" rx="3.5" fill="#dfecff" />
                            <circle cx="86" cy="18" r="5.5" fill="#0968fe" />
                        </g>
                    </g>
                    <g transform="translate(430 24)">
                        <g className="bv-flo is-d1">
                            <rect width="92" height="42" rx="9" fill="#ffffff" stroke="#e5e5e1" strokeWidth="1.5" />
                            <rect x="12" y="12" width="9" height="18" rx="2.5" fill="#00275e" />
                            <rect x="26" y="12" width="9" height="12" rx="2.5" fill="#a2c0eb" />
                            <rect x="40" y="12" width="9" height="18" rx="2.5" fill="#a2c0eb" />
                            <rect x="54" y="12" width="9" height="8" rx="2.5" fill="#dfecff" />
                        </g>
                    </g>
                    <g transform="translate(452 100)">
                        <g className="bv-flo is-d2">
                            <circle cx="20" cy="20" r="20" fill="#e9f5ef" />
                            <path d="M11.5 20.5 L17.5 26.5 L28.5 14.5" fill="none" stroke="#1f8a5b" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
                        </g>
                    </g>
                </svg>
            )}

            {passo === 2 && (
                <svg className="bv-cena" viewBox="0 0 672 160">
                    <g transform="translate(140 14)">
                        <rect width="64" height="122" rx="11" fill="#ffffff" stroke="#00275e" strokeWidth="2.5" />
                        <rect x="21" y="5" width="22" height="4" rx="2" fill="#00275e" />
                        <rect className="bv-pop" x="9" y="23" width="36" height="14" rx="7" fill="#dfecff" />
                        <rect className="bv-pop is-p2" x="19" y="43" width="36" height="14" rx="7" fill="#00275e" />
                        <rect className="bv-pop is-p3" x="9" y="63" width="30" height="14" rx="7" fill="#dfecff" />
                        <rect x="9" y="100" width="46" height="11" rx="5.5" fill="none" stroke="#e5e5e1" strokeWidth="1.5" />
                    </g>
                    <path className="bv-dash" d="M212 78 C 280 78, 340 78, 420 78" fill="none" stroke="#a2c0eb" strokeWidth="2" strokeDasharray="6 6" />
                    <circle r="5" fill="#0968fe">
                        <animateMotion dur="2.4s" repeatCount="indefinite" path="M212 78 C 280 78, 340 78, 420 78" />
                    </circle>
                </svg>
            )}

            {passo === 3 && (
                <svg className="bv-cena" viewBox="0 0 672 160">
                    {CONFETE.map((c, i) => (
                        <g key={i} transform={`translate(${c.x} ${c.y})`}>
                            <g className={`bv-fall is-f${i}`}>
                                <rect width={c.w} height={c.h} rx="1.5" fill={c.cor} />
                            </g>
                        </g>
                    ))}
                </svg>
            )}

            <div className="bv-brik">
                <div className="bv-brik-in">
                    <BrikAgente
                        tamanho="grande"
                        px={alturaBrik}
                        cara={passo === 1 ? 'feliz' : passo === 3 ? 'comemorando' : 'atento'}
                        vivo={passo === 2}
                    />
                </div>
            </div>
        </div>
    );
};

export default CenaBoasVindas;
